import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ModalWindow } from '../ModalWindow';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogText,
  DialogHelpText,
  DialogActions,
  DialogButton,
} from '../ModalWindow/dialogStyles';
import { Select as SelectNew } from '../NewSelect';
import { ConfirmedSelectModal } from './types';

type MultipleConfigDialogProps = {
  modal: ConfirmedSelectModal;
  count: number;
  onCancel: () => void;
  // Aplica a escolha nos itens selecionados; rejeitar mostra o feedback de erro e mantém a confirmação aberta.
  onApply: (selected: string) => Promise<void>;
};

// Ação de "Múltiplas Configurações" em dois passos: escolha → confirmação com a contagem → feedback.
export function MultipleConfigDialog({
  modal, count, onCancel, onApply,
}: Readonly<MultipleConfigDialogProps>): JSX.Element {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  function onContinue(): void {
    if (!count) {
      toast.warn(t('selecioneAoMenosUmItem'));
      return;
    }
    setConfirming(true);
  }

  async function onConfirm(choice: string): Promise<void> {
    setLoading(true);
    try {
      await onApply(choice);
      toast.success(modal.success(choice, count));
    } catch (err) {
      console.log(err);
      toast.error(modal.error(choice, count));
    }
    setLoading(false);
  }

  if (confirming && selected) {
    return (
      // key: cada passo é uma janela própria, sem reaproveitar a instância da escolha.
      <ModalWindow key="confirmation" variant="dialog" onClickOutside={() => setConfirming(false)}>
        <Dialog gap={16} data-testid="multiple-config-confirmation">
          <DialogTitle>{modal.confirmTitle(selected, count)}</DialogTitle>
          <DialogText>{modal.confirmDescription(selected)}</DialogText>
          <DialogActions>
            <DialogButton variant="primary-stroke" onClick={() => setConfirming(false)}>{t('voltarConfirmacao')}</DialogButton>
            <DialogButton variant="primary-filled" disabled={loading} onClick={() => { onConfirm(selected); }}>{t('confirmar')}</DialogButton>
          </DialogActions>
        </Dialog>
      </ModalWindow>
    );
  }

  const selectedOption = modal.options.find((option) => option.value === selected) ?? null;
  return (
    <ModalWindow key="choice" variant="dialog" onClickOutside={onCancel}>
      <Dialog data-testid="multiple-config-choice">
        <DialogHeader>
          <DialogTitle>{modal.name}</DialogTitle>
          <DialogText>{modal.countLabel(count)}</DialogText>
        </DialogHeader>
        <SelectNew
          options={modal.options}
          propLabel="name"
          value={selectedOption}
          label={modal.label}
          placeholder={modal.placeholder}
          notNull
          onSelect={(option: { value: string }) => setSelected(option.value)}
        />
        <DialogHelpText>{modal.helpText}</DialogHelpText>
        <DialogActions>
          <DialogButton variant="primary-stroke" onClick={onCancel}>{t('cancelar')}</DialogButton>
          <DialogButton variant="primary-filled" disabled={!selected} onClick={onContinue}>{t('continuar')}</DialogButton>
        </DialogActions>
      </Dialog>
    </ModalWindow>
  );
}
